import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, X, Save } from 'lucide-react';
import { WorkflowRule } from '../../../types/workflowRules';
import * as workflowRuleRouter from '../../../services/workflowRuleStorageRouter';
import { WizardStep1BasicInfo } from './wizard/WizardStep1BasicInfo';
import { WizardStep2Criteria } from './wizard/WizardStep2Criteria';
import { WizardStep3Actions } from './wizard/WizardStep3Actions';
import { WizardStep4Review } from './wizard/WizardStep4Review';

interface WorkflowRuleWizardProps {
  mode: 'new' | 'edit';
  ruleId?: string;
  onClose: () => void;
}

/**
 * WorkflowRuleWizard Component
 *
 * A 4-step wizard for creating and editing workflow rules:
 * - Step 1: Basic Information (name, object, trigger)
 * - Step 2: Define Criteria (conditions - placeholder for Chunk 4)
 * - Step 3: Configure Actions (actions - placeholder for Chunk 4)
 * - Step 4: Review & Confirm (summary before saving)
 *
 * @component
 */
export default function WorkflowRuleWizard({ mode, ruleId, onClose }: WorkflowRuleWizardProps) {
  const isEdit = mode === 'edit';

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<WorkflowRule>>(() => getDefaultFormData());

  useEffect(() => {
    if (isEdit && ruleId) {
      workflowRuleRouter.getRuleAsync(ruleId).then(rule => {
        setFormData(rule || getDefaultFormData());
      });
    }
  }, [isEdit, ruleId]);

  // Get default form data
  function getDefaultFormData(): Partial<WorkflowRule> {
    return {
      name: '',
      description: '',
      objectApiName: '',
      isActive: true,
      triggerType: 'onCreateAndUpdate',
      evaluationCriteria: {
        operator: 'AND',
        conditions: []
      },
      actions: [],
      createdBy: 'current_user', // TODO: Get from auth context
      lastModifiedBy: 'current_user'
    };
  }

  // Steps configuration
  const steps = [
    { number: 1, title: 'Basic Information', component: WizardStep1BasicInfo },
    { number: 2, title: 'Define Criteria', component: WizardStep2Criteria },
    { number: 3, title: 'Configure Actions', component: WizardStep3Actions },
    { number: 4, title: 'Review & Confirm', component: WizardStep4Review }
  ];

  // Update form data
  const updateFormData = (updates: Partial<WorkflowRule>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Navigation handlers
  const handleNext = () => {
    // Trigger the step's validation by clicking its hidden button
    // Each step component has a hidden button with ID pattern: stepN-next-btn
    const stepButton = document.getElementById(`step${currentStep}-next-btn`);
    if (stepButton) {
      console.log(`🎯 Wizard: Triggering Step ${currentStep} validation`);
      stepButton.click();
    } else {
      // Fallback: if no validation button, just proceed
      console.log(`⚠️ No validation button found for Step ${currentStep}, proceeding anyway`);
      proceedToNextStep();
    }
  };

  const proceedToNextStep = () => {
    if (currentStep < steps.length) {
      console.log(`✅ Wizard: Moving from Step ${currentStep} to Step ${currentStep + 1}`);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      onClose();
    }
  };

  const handleSave = async () => {
    try {
      if (isEdit && ruleId) {
        const updated = await workflowRuleRouter.updateRuleAsync(ruleId, formData);
        if (updated) {
          alert('✅ Workflow rule updated successfully!');
          onClose();
        } else {
          alert('❌ Failed to update workflow rule.');
        }
      } else {
        const created = await workflowRuleRouter.createRuleAsync(
          formData as Omit<WorkflowRule, 'id' | 'createdDate' | 'lastModified'>
        );
        if (created) {
          alert('✅ Workflow rule created successfully!');
          onClose();
        } else {
          alert('❌ Failed to create workflow rule.');
        }
      }
    } catch (error) {
      console.error('Error saving workflow rule:', error);
      alert('❌ An error occurred while saving the workflow rule.');
    }
  };

  // Get current step component
  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Workflow Rule' : 'New Workflow Rule'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}
          </p>
        </div>
        <button
          onClick={handleCancel}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          title="Cancel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                    ${step.number === currentStep
                      ? 'bg-blue-600 text-white'
                      : step.number < currentStep
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }
                  `}
                >
                  {step.number < currentStep ? '✓' : step.number}
                </div>
                <div className={`
                  mt-2 text-xs font-medium text-center max-w-[100px]
                  ${step.number === currentStep
                    ? 'text-blue-600 dark:text-blue-400'
                    : step.number < currentStep
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
                  }
                `}>
                  {step.title}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className="flex-1 h-1 mx-4 bg-gray-200 dark:bg-gray-700 rounded">
                  <div
                    className="h-full bg-blue-600 rounded transition-all duration-300"
                    style={{
                      width: step.number < currentStep ? '100%' : '0%'
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <CurrentStepComponent
          formData={formData}
          updateFormData={updateFormData}
          onNext={proceedToNextStep}
          onBack={handleBack}
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
            ${currentStep === 1
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }
          `}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="text-sm text-gray-500 dark:text-gray-400">
          Step {currentStep} of {steps.length}
        </div>

        {currentStep < steps.length ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Next
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Save className="w-4 h-4" />
            {isEdit ? 'Update Rule' : 'Save & Activate'}
          </button>
        )}
      </div>

      {/* Cancel Button */}
      <div className="text-center">
        <button
          onClick={handleCancel}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          Cancel and return to Workflow Rules
        </button>
      </div>
    </div>
  );
}
